import * as React from "react";
import "./TreeView.scss";
import { TreeView as BaseTreeView, TreeViewNode, TreeViewProps as BaseTreeViewProps, TreeItemPosition, getItemIdFromNode, LoadingContainer } from "../../../shared/components";
import * as _ from "lodash";
import { useSelector } from "react-redux";
import { RootState, useThunkDispatch } from "../../../store";
import { updateParameterValue } from "../../../store/storyline/actions";
import { showError } from "../../../store/notifications/actions";
import { DocumentedComponent } from "../../../shared/components/DocumentedComponent";
import { type WithDataProps } from "../../../shared/utilities";
import { useLazyData } from "../../../shared/hooks";
import type { SourceInfo } from "@recursive-robot/react-jsx-parser";
import { useReportValidationError } from "../../../shared/hooks/useReportValidationError";

function getFullHierarchy(node: TreeViewNode): TreeViewNode[] {
    const allChildren = _.flatMap(node.children, getFullHierarchy);
    return [node, ...allChildren];
}

function addBackwardsCompatibility(node: TreeViewNode) {
    node.value = node.value ?? node["id"];
    node.label = node.label ?? node["title"];
}

function validateNodes(allNodes: TreeViewNode[], getNodeId: (node: TreeViewNode) => string): NodeValidationErrors[] {
    const errors: NodeValidationErrors[] = [];
    const idSet = new Set<string>();
    allNodes.forEach(node => {
        const nodeId = getNodeId(node);
        if (nodeId == null) {
            errors.push({
                type: "null-ids",
                message: `Node "${node.label}" has a null ID due to having a null/undefined value. If this is intentional, please provide a custom \`getNodeId\` function to generate unique IDs for each node.`,
            });
        } else if (idSet.has(nodeId)) {
            // find original node with this ID
            const originalNode = allNodes.find(n => getNodeId(n) === nodeId);
            errors.push({
                type: "duplicate-ids",
                message: `Node ID "${nodeId}" is duplicated for node "${node.label}" and "${originalNode.label}". Please ensure all nodes have unique IDs, or provide a custom \`getNodeId\` function to generate unique IDs for each node.`,
            });
        }
        idSet.add(nodeId);
    });
    return errors;
}

type TreeViewProps = WithDataProps<BaseTreeViewProps<TreeViewNode>> & {
    onNodeSelect?: (selectedNodes: TreeViewNode[]) => void;
    isNodeEditable?: boolean | ((node: TreeViewNode) => boolean);
    getNodeId?: (node: TreeViewNode) => string;
    getNodes?: (args?: { parentId?: string }) => TreeViewNode[] | Promise<TreeViewNode[]>;
    lazyChildren?: boolean;
    sourceInfo?: SourceInfo;
};

type NodeValidationErrors = {
    type: "duplicate-ids" | "null-ids";
    message: string;
}

function TreeView(props: TreeViewProps) {
    const { nodes, onNodeSelect, multiSelect, name, showSearch, onLabelChange, isNodeEditable, onNodeReorder, getNodeDropAllowed, getNodeId: _getNodeId, getNodes, lazyChildren, sourceInfo, ...rest } = props;
    const _parameterValue = useSelector((s: RootState) => s.storyline.parameterValues?.get?.(name));
    const parameterValue = _parameterValue ?? (multiSelect ? [] : null);
    const [defaultExpanded, setDefaultExpanded] = React.useState<unknown[]>([]);
    const [defaultSelected, setDefaultSelected] = React.useState<unknown[]>(multiSelect ? parameterValue as unknown[] : [parameterValue]);
    const [selected, setSelected] = React.useState<unknown[]>([]);
    const [currentNodes, setCurrentNodes] = React.useState([]);
    const [validationErrors, setValidationErrors] = React.useState<NodeValidationErrors[]>([]);
    const dispatch = useThunkDispatch();
    const getNodeId = _getNodeId ?? getItemIdFromNode;
    const reportValidationError = useReportValidationError(sourceInfo, "TreeView");

    const lazyMode = !!getNodes && !!lazyChildren;
    const oneShotMode = !!getNodes && !lazyChildren;
    const { data: fetchedNodes, isLoading, hasLoaded, load } = useLazyData<TreeViewNode[]>({ fetcher: getNodes, errorMessage: "Failed to load tree nodes." });

    React.useEffect(() => {
        if (getNodes && nodes) {
            reportValidationError("Both `nodes` and `getNodes` were provided - `nodes` will be ignored.");
        }

        if (lazyChildren && !getNodes) {
            reportValidationError("`lazyChildren` requires `getNodes` and will be ignored.");
        }
    }, []);

    // In one-shot mode the full tree is fetched once on mount; all existing flatten/validate/search logic then applies as usual...
    React.useEffect(() => {
        if (oneShotMode) load();
    }, [oneShotMode]);

    // Memoized so the fallback `[]` keeps a stable identity - a fresh array here would re-trigger the
    // full-hierarchy effect below on every render...
    const effectiveNodes = React.useMemo(() => oneShotMode ? (fetchedNodes ?? []) : nodes, [oneShotMode, fetchedNodes, nodes]);

    // Keep the latest `getNodes` in a ref - template-supplied arrows are recreated on every render...
    const getNodesRef = React.useRef(getNodes);
    getNodesRef.current = getNodes;

    // In lazy mode, children are fetched on node expansion via MUI's data source API...
    const dataSource = React.useMemo(() => {
        if (!lazyMode) return undefined;

        return {
            // MUI treats an item as expandable when the count is > 0, so `hasChildren` maps to a nominal count of 1...
            getChildrenCount: (item: TreeViewNode) => item.childrenCount ?? (item.hasChildren ? 1 : (item.children?.length ?? 0)),
            getTreeItems: async (parentId?: string) => {
                try {
                    const result = await Promise.resolve(getNodesRef.current({ parentId }));
                    return (result ?? []).map(node => {
                        addBackwardsCompatibility(node);
                        return { ...node, id: getNodeId(node) };
                    });
                }
                catch (error: any) {
                    dispatch(showError(`Failed to load tree nodes: ${String(error?.message ?? error)}`));
                    throw error; // Let MUI mark the item as errored (and retryable)...
                }
            }
        };
    }, [lazyMode, getNodeId, dispatch]);

    React.useEffect(() => {
        // The full-hierarchy pipeline (validation, default expansion/selection, search) requires the whole tree
        // up-front and is bypassed in lazy mode...
        if (lazyMode) return;

        const allNodes = Array.isArray(effectiveNodes) ? _.flatMap(effectiveNodes, getFullHierarchy) : [];
        allNodes?.forEach?.(addBackwardsCompatibility);

        const errors = validateNodes(allNodes, getNodeId);
        setValidationErrors(errors);

        if (currentNodes?.length === effectiveNodes?.length && _.isEqual(currentNodes, effectiveNodes)) return;
        setCurrentNodes(effectiveNodes);

        let selected = [];
        if (parameterValue != undefined) {
            selected = multiSelect ? parameterValue as unknown[] : [parameterValue];
        }
        const defaultExpandedNodes = allNodes.filter(n => n.isExpanded).map(n => getNodeId(n));
        setDefaultExpanded(_.uniq(defaultExpandedNodes));

        const defaultSelectedNodeIds = allNodes.filter(n => n.isSelected).map(n => getNodeId(n));
        const allSelectedNodeIds = _.uniq([...selected, ...defaultSelectedNodeIds]);
        setDefaultSelected(allSelectedNodeIds);
    }, [effectiveNodes, getNodeId, lazyMode]);

    React.useEffect(() => {
        if (_parameterValue === undefined) return;

        let newSelected: any[] = multiSelect ? parameterValue as unknown[] : [parameterValue];
        if (selected?.length !== newSelected?.length || !_.isEqual(selected, newSelected)) {
            setSelected(newSelected);
        }
    }, [_parameterValue]);

    const handleSelect = React.useCallback((selectedNodes: TreeViewNode[]) => {
        if (onNodeSelect) {
            onNodeSelect(selectedNodes);
        }

        if (name) {
            dispatch(updateParameterValue(name, multiSelect ? selectedNodes.map(n => n.value) : selectedNodes?.[0]?.value));
        }
    }, [onNodeSelect, name, multiSelect]);

    const handleNodeEditable = React.useCallback((node: TreeViewNode): boolean => {
        if (_.isBoolean(isNodeEditable)) return isNodeEditable;
        if (_.isFunction(isNodeEditable)) return isNodeEditable(node);
        return node.isEditable;
    }, [isNodeEditable]);

    const handleLabelChange = React.useCallback((node: TreeViewNode, newLabel: string) => {
        if (onLabelChange) {
            // prevent default behavior if the callback returns false
            if (onLabelChange(node, newLabel) === false) return;
        }

        // default logic
        // find node in currentNodes and update label
        const allNodes = Array.isArray(currentNodes) ? _.flatMap(currentNodes, getFullHierarchy) : [];
        let oldNode = allNodes.find(n => _.isEqual(n.value, node.value));
        if (oldNode) {
            oldNode.label = newLabel;
            setCurrentNodes([...currentNodes]);
        }
    }, [onLabelChange, currentNodes]);

    const handleNodeReorder = React.useCallback((node: TreeViewNode, oldPosition: TreeItemPosition, newPosition: TreeItemPosition) => {
        if (onNodeReorder) {
            // prevent default behavior if the callback returns false
            if (onNodeReorder(node, oldPosition, newPosition) === false) return;
        }

        // default logic
        // first find the old parent and remove the node from its children
        const allNodes = Array.isArray(currentNodes) ? _.flatMap(currentNodes, getFullHierarchy) : [];
        const oldNode = allNodes.find(n => _.isEqual(n.value, node.value));
        if (!oldNode) return;
        const oldParent = allNodes.find(n => _.isEqual(n.value, oldPosition.parentId));
        if (oldParent) {
            // we are given the old index, thus no need for searching
            oldParent.children?.splice(oldPosition.index, 1);
        } else if (oldPosition.parentId == null) {
            // we have the index to remove from root
            if (oldPosition.index >= 0 && oldPosition.index < currentNodes.length) {
                currentNodes.splice(oldPosition.index, 1);
            }
        }

        // find the new parent and add the node to its children
        // if the parentId is null, add to root
        const newParent = allNodes.find(n => _.isEqual(n.value, newPosition.parentId));
        if (newParent) {
            // we are given the new index, thus no need for searching
            if (!newParent.children) {
                newParent.children = [];
            }
            newParent.children.splice(newPosition.index, 0, oldNode);
        } else if (newPosition.parentId == null) {
            // add to root
            currentNodes.push(oldNode);
        }

        setCurrentNodes([...currentNodes]);
    }, [onNodeReorder, currentNodes]);

    const handleGetNodeDropAllowed = React.useCallback((node: TreeViewNode, oldPosition: TreeItemPosition, newPosition: TreeItemPosition): boolean => {
        if (getNodeDropAllowed) {
            return getNodeDropAllowed(node, oldPosition, newPosition);
        }
        return true;
    }, [getNodeDropAllowed]);

    const inputProps = {
        ...rest,
        name: name,
        defaultExpandedItems: defaultExpanded,
        defaultSelectedItems: multiSelect ? defaultSelected : defaultSelected?.[0],
        selectedItems: multiSelect ? selected : selected?.[0],
        nodes: currentNodes,
        multiSelect: multiSelect,
        onSelect: handleSelect,
        showSearch: showSearch,
        onLabelChange: handleLabelChange,
        isItemEditable: handleNodeEditable,
        onNodeReorder: handleNodeReorder,
        getNodeDropAllowed: handleGetNodeDropAllowed,
        getItemId: getNodeId,
        dataSource: dataSource,
    } as TreeViewProps;

    // Show a loading indicator while the one-shot `getNodes` fetch is in flight...
    if (oneShotMode && (isLoading || !hasLoaded)) {
        return <LoadingContainer isLoading type="circular">{null}</LoadingContainer>;
    }

    if (validationErrors.length > 0) {
        return <div className="treeview-validation-errors" data-testid="treeview-validation-errors">
            <ul>
                {validationErrors.map(e => <li>{e.message}</li>)}
            </ul>
        </div>;
    }

    return (
        <BaseTreeView
            {...inputProps}
        />
    );
}

(TreeView as DocumentedComponent).injectSourceInfo = true;
(TreeView as DocumentedComponent).metadata = {
    description: "The `TreeView` component renders hierarchical data in a collapsible tree structure.",
    isSelfClosing: true,
    attributes: [
        { name: "name", type: "string", description: "The name of the parameter to persist the selected Node Value(s) to.  If `multiselect` is enabled, the persisted value will be an array, otherwise it is a string value. Optional - automated parameter read/write behaviour is disabled if the parameter name is not specified, but the `onNodeSelect` callback can still be used for this purpose. The underlying component's `data-testid` attribute will be set to this value. This is useful for targeting the element via Playwright, Puppeteer or similar UI testing tools. Can be overridden via an explicit `data-testid` value." },
        { name: "multiSelect", type: "boolean", description: "Enables multi-select mode for the tree view. The user must ctrl-click to multiselect, unless `multiSelectWithoutCtrl` is `true`. Optional - defaults to `false`." },
        { name: "multiSelectWithoutCtrl", type: "boolean", description: "Allows multi-select mode for the tree view without the need to ctrl-click. Optional - defaults to `false`." },
        { name: "showSearch", type: "boolean", description: "If `true`, renders a text field that allows the user to search within the TreeView. Keep in mind you would need to account for this in your custom tree node rendering. Optional - defaults to `false`." },
        { name: "defaultSearchTerm", type: "string", description: "The default search term to use when the search field is first rendered. Optional - defaults to `''`." },
        { name: "onNodeSelect", type: "function", exactType: "(selectedNodes: TreeViewNode[]) => void", template: `onNodeSelect={(selectedNodes) => {$1}}`, description: "The callback function to execute when a node is selected. If `multiSelect` is `false`, the input parameter is a single-element array." },
        { name: "onLabelChange", type: "function", exactType: "(node: TreeViewNode, newLabel: string) => boolean | void", template: `onLabelChange={(node, newLabel) => {$1}}`, description: "The callback function to execute when a node's label is changed (only applicable if nodes are editable). If not provided, the component will update the label in-place. If the callback returns `false`, the label change will not be persisted to the node structure passed to `nodes`." },
        { name: "isNodeEditable", type: "function", exactType: "boolean | (node: TreeViewNode) => boolean", template: `isNodeEditable={(node) => $1}`, description: "If a boolean value is provided, controls whether all nodes are editable. If a function is provided, it is called for each node to determine if that specific node is editable. If not provided, the node's `isEditable` field is used. Refer to the [MUI](https://mui.com/x/react-tree-view/rich-tree-view/editing/) documentation for more details." },
        { name: "itemsReordering", type: "boolean", description: "If `true`, enables drag-and-drop reordering of tree nodes. Refer to [MUI](https://mui.com/x/react-tree-view/rich-tree-view/ordering/) documentation for more details. Optional - defaults to `false`." },
        { name: "getNodeDropAllowed", type: "function", exactType: "(node: TreeViewNode, oldPosition: TreeItemPosition, newPosition: TreeItemPosition) => boolean", template: `getNodeDropAllowed={(node, oldPosition, newPosition) => {$1}}`, description: "The callback function to execute to determine if a node can be moved from its old position to the new position. If not provided, the component will allow all moves if `itemsReordering` is `true`." },
        { name: "getNodeId", type: "function", exactType: "(node: TreeViewNode) => string", template: `getNodeId={(node) => $1}`, description: "The callback function to retrieve the unique ID of a node. If not provided, the component will use the node's `value` field to generate the id." },
        { name: "editNodeIcon", type: "object", exactType: "React.ReactNode", description: "The icon to use for the 'edit node' action.  Optional - defaults to a `Pencil` icon." },
        { name: "acceptEditNodeIcon", type: "object", exactType: "React.ReactNode", description: "The icon to use for the 'accept edit node' action.  Optional - defaults to a `Check` icon." },
        { name: "cancelEditNodeIcon", type: "object", exactType: "React.ReactNode", description: "The icon to use for the 'cancel edit node' action. Optional - defaults to an `X` icon." },
        { name: "dragIcon", type: "object", exactType: "React.ReactNode", description: "The icon to use as the drag handle for reordering nodes.  Optional - defaults to a `DragVertical` icon." },
        { name: "onNodeReorder", type: "function", exactType: "(node: TreeViewNode, oldPosition: TreeItemPosition, newPosition: TreeItemPosition) => boolean | void", template: `onNodeReorder={(node, oldPosition, newPosition) => {$1}}`, description: "The callback function to execute when a node is reordered via drag-and-drop. If not provided, the component will update the node position in-place. If the callback returns `false`, the reorder will not be persisted to the node structure passed to `nodes`." },
        {
            name: "nodes",
            type: "object",
            exactType: "{ value: any, label: string, children?: TreeViewNode[], isExpanded?: boolean, isSelected?: boolean, preventSelection?: boolean, additionalSearchTerms?: string[] }[]",
            description: `The nodes to display in the tree.  Alternatively, use \`getNodes\` to fetch the nodes lazily.  See below for the structure of \`TreeViewNode\`.

### \`TreeViewNode\` Fields:

| Name | Type | Description |
|------|------|-------------|
| \`id\` | \`string\` | The unique identifier for this node.  If not provided, a unique ID will be generated using \`getNodeId\` or \`node.value\`. |
| \`depth\` | \`number\` | The depth of this node in the tree. Starts at \`1\` for the root nodes. |
| \`value\` | \`any\` | The value of the tree node.  Used to identify a specific node and is the value assigned to the parameter value when this node is selected. |
| \`label\` | \`string\` | The text content of this tree node. |
| \`children\` | \`TreeViewNode[]\` | The children of this node (optional). |
| \`isExpanded\` | \`boolean\` | Controls whether this node is expanded by default.  Only applies to nodes with children.  Optional - defaults to \`false\`. |
| \`isSelected\` | \`boolean\` | Controls whether this node is selected by default.  Optional - defaults to \`false\`. |
| \`isEditable\` | \`boolean\` | Controls whether this node's label is editable.  Optional - defaults to \`false\`. |
| \`preventSelection\` | \`boolean\` | If \`true\`, this node cannot be selected.  Optional - defaults to \`false\`. |
| \`additionalSearchTerms\` | \`string[]\` | An optional array of additional search terms (in addition to the \`label\`) which will be matched against when searching for nodes.  Optional - defaults to \`null\`. |
` },
        { name: "multiSelect", type: "boolean", description: "If `true`, allows the user to select multiple items by holding down `Ctrl` or `Shift`.  Optional - defaults to `false`." },
        { name: "showSearch", type: "boolean", description: "If `true`, renders a text field that allows the user to search within the TreeView.  Optional - defaults to `false`." },
        { name: "onNodeSelect", type: "function", exactType: "(selectedNodes: { value: string, label: string, children?: TreeViewNode[], preventSelection?: boolean, additionalSearchTerms?: string[], [key: string]: any }[]) => void", template: `onNodeSelect={(selectedNodes) => {$1}}`, description: "The callback function to execute when a node is selected.  If `multiSelect` is `false`, the input parameter is a single-element array." },
        { name: "renderNode", type: "function", exactType: "(node: { value: string, label: string, children?: TreeViewNode[], preventSelection?: boolean, additionalSearchTerms?: string[], [key: string]: any }, depth: number, searchTerm?: string) => React.ReactNode", template: `renderNode={(node, depth) => $1}`, description: "An optional callback function which can be used to customize the label for each node.  Invoked for each item in the tree, passing in the current item and its depth in the tree.  Optional - defaults to rendering the label (as a text element) for each item." },
        { name: "getNodeTooltip", type: "function", exactType: "(node: { value: string, label: string, children?: TreeViewNode[], preventSelection?: boolean, additionalSearchTerms?: string[], [key: string]: any }) => React.ReactNode", template: `getNodeTooltip={(node) => {$1}}`, description: "The (optional) callback function used to provide tooltip content for each node.  If this function returns a `falsey` value, no tooltip will be shown for that particular node.  Optional - defaults to `null`." },
        { name: "getNodeSelectionDisabled", type: "function", exactType: "(node: { value: string, label: string, children?: TreeViewNode[], preventSelection?: boolean, additionalSearchTerms?: string[], [key: string]: any }) => boolean", template: `getNodeSelectionDisabled={(node) => {$1}}`, description: "The (optional) callback function used to determine if a node can be selected.  If this function returns `true`, the node cannot be selected.  Optional - defaults to `false`." },
        { name: "maxSelectionCount", type: "number", description: "The maximum number of nodes that can be selected.  Only applies when `multiSelect` is `true`.  Optional - defaults to unlimited." },
        {
            name: "getNodes",
            type: "function",
            exactType: "(args?: { parentId?: string }) => { value: any, label: string, children?: TreeViewNode[], childrenCount?: number, hasChildren?: boolean, [key: string]: any }[] | Promise<{ value: any, label: string, children?: TreeViewNode[], childrenCount?: number, hasChildren?: boolean, [key: string]: any }[]>",
            template: `getNodes={(args) => $1}`,
            description: `A callback function which lazily fetches the tree nodes.  Serves as an alternative to \`nodes\` - if both are provided, \`getNodes\` takes precedence and \`nodes\` is ignored.

The callback may return the nodes directly, or a \`Promise\` resolving to them.  **Note:** \`async\` arrow functions are not supported by the template parser - write a regular arrow function which *returns* a \`Promise\` instead, e.g. \`getNodes={(args) => fetchDatasourceByName("myDatasource", args)}\`.

By default, the callback is invoked once (without arguments) on initial render and must return the full tree; all other features (search, validation, default expansion/selection) work as usual.  When \`lazyChildren\` is enabled, the callback is instead invoked per node expansion with \`{ parentId }\` (\`parentId\` is \`undefined\` for the root nodes) and must return only that node's direct children - each returned node must then include either a \`childrenCount\` field (the number of children) or a \`hasChildren\` boolean, so that the expand arrow can be rendered before the children are fetched.

A loading indicator is displayed while a fetch is in progress.  If a fetch fails, an error notification is shown.`
        },
        {
            name: "lazyChildren",
            type: "boolean",
            description: "Set this property to `true` in order to fetch children lazily (per node expansion) via `getNodes`, instead of fetching the full tree up-front.  Requires `getNodes` to be provided.  In this mode `showSearch`, node validation and the `isExpanded`/`isSelected` node defaults are not supported, and fetched children are cached for the lifetime of the component (re-mount the component via a changed `key` to force a refresh).  Optional - defaults to `false`."
        },
    ]
};

export { TreeView };